"use client";

import { useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";

interface PasskeyButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

export default function PasskeyButton({ onSuccess, onError, className }: PasskeyButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    try {
      const optionsRes = await fetch("/api/auth/passkey/authenticate");
      if (!optionsRes.ok) {
        throw new Error("Failed to get authentication options");
      }
      const options = await optionsRes.json();

      const credential = await startAuthentication({ optionsJSON: options });

      const verifyRes = await fetch("/api/auth/passkey/authenticate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          response: credential,
          sessionId: options.sessionId,
        }),
      });

      const result = await verifyRes.json();

      if (!verifyRes.ok) {
        throw new Error(result.error || "Authentication failed");
      }

      onSuccess?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Passkey login failed";
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handlePasskeyLogin}
      disabled={isLoading}
      className={className || "w-full py-3 px-4 bg-(--bg-secondary) text-(--text-primary) font-semibold rounded-lg hover:bg-(--border-color) transition-colors disabled:opacity-50 flex items-center justify-center gap-2"}
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 1C8.676 1 6 3.676 6 7c0 2.325 1.327 4.345 3.262 5.343.082.042.169.074.258.097A3.001 3.001 0 0 0 12 18a3.001 3.001 0 0 0 2.48-5.56c.089-.023.176-.055.258-.097C16.673 11.345 18 9.325 18 7c0-3.324-2.676-6-6-6zm0 2c2.243 0 4 1.757 4 4s-1.757 4-4 4-4-1.757-4-4 1.757-4 4-4zm0 10c-.552 0-1 .448-1 1s.448 1 1 1 1-.448 1-1-.448-1-1-1zm-5 4c-1.657 0-3 1.343-3 3v2h16v-2c0-1.657-1.343-3-3-3H7z"/>
      </svg>
      {isLoading ? "Authenticating..." : "Sign in with Passkey"}
    </button>
  );
}
